import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo } from 'react';
import { useGetTaskWidgetKPIs } from '@/shared/hooks/task/useGetTaskWidgetKPIs';
import { useDataContext } from '@/shared/providers/DataProvider';
import { useStateService } from '@/shared/services/StateService';
import LoadingContainer from '@/shared/components/loading/LoadingContainer';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { MyTasksWidgetKPIOptions } from '../../../tasks/taskboard/constants';
import DashboardTaskKpiTile from '../partials/DashboardTaskKpiTile';
import messages from '../../dashboardMessages';
import { capacityPlanningOptions } from '@/modules/home/dashboards/MyTaskKpiDashboard';
const formatMoment = (momentValue) => momentValue.format('YYYY-MM-DD');
const getDeadlineFilter = (kpi) => {
    const now = moment.utc();
    if (kpi === MyTasksWidgetKPIOptions.pastDeadline) {
        return {
            deadlineFrom: null,
            deadlineUntil: formatMoment(now.clone().add(-1, 'days')),
        };
    }
    if (kpi === MyTasksWidgetKPIOptions.deadlineOneWeek) {
        return {
            deadlineFrom: formatMoment(now.clone()),
            deadlineUntil: formatMoment(now.clone().add(7, 'days')),
        };
    }
    if (kpi === MyTasksWidgetKPIOptions.deadlineTwoWeeks) {
        return {
            deadlineFrom: formatMoment(now.clone().add(7, 'days')),
            deadlineUntil: formatMoment(now.clone().add(14, 'days')),
        };
    }
    if (kpi === MyTasksWidgetKPIOptions.deadlineThreeWeeks) {
        return {
            deadlineFrom: formatMoment(now.clone().add(14, 'days')),
            deadlineUntil: formatMoment(now.clone().add(21, 'days')),
        };
    }
    if (kpi === MyTasksWidgetKPIOptions.deadlineFourWeeks) {
        return {
            deadlineFrom: formatMoment(now.clone().add(21, 'days')),
            deadlineUntil: formatMoment(now.clone().add(28, 'days')),
        };
    }
    if (kpi === MyTasksWidgetKPIOptions.deadlineThreeMonths) {
        return {
            deadlineFrom: formatMoment(now.clone().add(28, 'days')),
            deadlineUntil: formatMoment(now.clone().add(3, 'months')),
        };
    }
    if (kpi === MyTasksWidgetKPIOptions.deadlineSixMonths) {
        return {
            deadlineFrom: formatMoment(now.clone().add(3, 'months')),
            deadlineUntil: formatMoment(now.clone().add(6, 'months')),
        };
    }
    if (kpi === MyTasksWidgetKPIOptions.deadlineOneYear) {
        return {
            deadlineFrom: formatMoment(now.clone().add(6, 'months')),
            deadlineUntil: formatMoment(now.clone().add(12, 'months')),
        };
    }
    return {};
};
export const MultipleTaskKpiTiles = ({ configuredKpis }) => {
    const { formatMessage } = useIntl();
    const { currentUserId } = useDataContext();
    const { data: kpiData, error, isLoading, refetch } = useGetTaskWidgetKPIs(configuredKpis);
    useEffect(() => {
        refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configuredKpis]);
    const [deadlineKpis, capacityKpis] = useMemo(() => {
        if (!configuredKpis)
            return [[], []];
        return configuredKpis
            .filter((x) => x !== MyTasksWidgetKPIOptions.nextUserIsMe)
            .reduce((result, x) => {
            result[capacityPlanningOptions.indexOf(x) < 0 ? 0 : 1].push(x);
            return result;
        }, [[], []]);
    }, [configuredKpis]);
    const stateService = useStateService();
    const getStartOfWeek = (k) => {
        switch (k) {
            case MyTasksWidgetKPIOptions.executionDateThisWeek:
                return moment().startOf('isoWeek');
            case MyTasksWidgetKPIOptions.executionDateNextWeek:
                return moment().startOf('isoWeek').add(7, 'days');
            case MyTasksWidgetKPIOptions.executionDateInTwoWeeks:
                return moment().startOf('isoWeek').add(14, 'days');
            case MyTasksWidgetKPIOptions.executionDateInThreeWeeks:
                return moment().startOf('isoWeek').add(21, 'days');
        }
    };
    const handleClick = (k) => {
        var _a;
        if (k === MyTasksWidgetKPIOptions.executionDateBeforeThisWeek) {
            stateService.gotoTaskList({
                userId: currentUserId,
                executionDateFrom: null,
                executionDateUntil: moment().add(-1, 'days').format('YYYY-MM-DD'),
                taskLevels: [0, 1],
                mainStatus: [5],
                period: 'all',
                userIsNextAssignedUser: configuredKpis.includes(MyTasksWidgetKPIOptions.nextUserIsMe),
            });
        }
        else if (capacityPlanningOptions.indexOf(k) >= 0) {
            stateService.gotoWeekPlanner({
                userId: currentUserId,
                executionDateFrom: (_a = getStartOfWeek(k)) === null || _a === void 0 ? void 0 : _a.format('YYYY-MM-DD'),
            });
        }
        else {
            stateService.gotoTaskboard(Object.assign({ userId: currentUserId, onlySubtasksForAssignedUser: true, userIsNextAssignedUser: configuredKpis.includes(MyTasksWidgetKPIOptions.nextUserIsMe) }, getDeadlineFilter(k)));
        }
    };
    return (_jsx("div", Object.assign({ className: "tasks-kpi-tiles" }, { children: _jsxs(LoadingContainer, Object.assign({ isLoading: isLoading }, { children: [!isLoading && error && _jsx("div", { children: formatMessage(messages.ErrorWhileRetrievingMyTaskKpis) }), !isLoading && !error && (_jsxs(_Fragment, { children: [deadlineKpis.length > 0 ? (_jsx("div", { children: _jsx("div", Object.assign({ className: "tile-grid" }, { children: deadlineKpis.map((x) => {
                                    var _a;
                                    return (_jsx(DashboardTaskKpiTile, { value: (_a = kpiData === null || kpiData === void 0 ? void 0 : kpiData[x]) !== null && _a !== void 0 ? _a : 0, kpi: x, onClick: handleClick }, x));
                                }) })) })) : null, capacityKpis.length > 0 ? (_jsx("div", { children: _jsx("div", Object.assign({ className: "tile-grid" }, { children: capacityKpis.map((x) => {
                                    var _a;
                                    return (_jsx(DashboardTaskKpiTile, { value: (_a = kpiData === null || kpiData === void 0 ? void 0 : kpiData[x]) !== null && _a !== void 0 ? _a : 0, kpi: x, onClick: handleClick }, x));
                                }) })) })) : null] }))] })) })));
};
